/**
 * Background Agent Controller
 *
 * Manages a dedicated background agent that handles non-interactive tasks:
 * - Learning module generation (MEMORY.md, SOUL.md, etc.)
 * - Memory auto-save after idle periods
 * - Creative thinking cycles
 *
 * The background agent runs separately from the main conversational agent
 * so background work doesn't pollute the user's chat history.
 */

import type { GatewayBrowserClient } from "../gateway.ts";
import type { AgentsListResult } from "../types.ts";

/** Well-known agent ID for background tasks. */
export const BACKGROUND_AGENT_ID = "background";

/** Session key used for all background agent tasks. */
export const BACKGROUND_SESSION_KEY = `agent:${BACKGROUND_AGENT_ID}:main`;

export type BackgroundAgentState = {
  client: GatewayBrowserClient | null;
  connected: boolean;
  agentsList: AgentsListResult | null;
};

/**
 * Ensure the background agent exists, creating it if necessary.
 * Call this after gateway hello + agents.list completes.
 * Returns true when the background agent is confirmed available.
 */
export async function ensureBackgroundAgent(state: BackgroundAgentState): Promise<boolean> {
  if (!state.client || !state.connected) {
    return false;
  }

  const agents = state.agentsList?.agents ?? [];
  const exists = agents.some((a) => a.id === BACKGROUND_AGENT_ID);
  if (exists) {
    return true;
  }

  try {
    await state.client.request("agents.create", {
      name: BACKGROUND_AGENT_ID,
      workspace: "~/.nabe/workspace",
      emoji: "⚙️",
    });
    // Reload agents list so subsequent code sees the new agent.
    try {
      const res = await state.client.request<AgentsListResult>("agents.list", {});
      if (res) {
        state.agentsList = res;
      }
    } catch {
      // Non-critical; the agent was created successfully.
    }
    return true;
  } catch (err) {
    // Agent may already exist (race condition) — that's fine.
    if (String(err).includes("already exists")) {
      return true;
    }
    console.warn("[background-agent] failed to create background agent:", err);
    return false;
  }
}
